import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  useReactTable,
  getCoreRowModel,
  getSortedRowModel,
  getFilteredRowModel,
  createColumnHelper,
  SortingState,
  ColumnFiltersState,
  flexRender,
} from "@tanstack/react-table";
import { PointItem } from "../types/PointItem";
import { ensureDefaultPointItems, listPointItems, upsertPointItem } from "../services/points";
import Popover from "../components/Popover";
import { buttonStyle, buttonBackgroundColor } from "../components/Style";
import PageLayout from "../components/PageLayout";
import TableInput from "../components/TableInput";
import Dropdown from "../components/Dropdown";
import Badge from "../components/Badge";
import {
  tanstackTableStyle,
  getTanstackThStyle,
  sortHeaderStyle,
  sortArrowStyle,
  filterContainerStyle,
  tanstackTrStyle,
  getTanstackTdStyle,
  clickableCellStyle,
  emptyRowStyle,
} from "../components/Style";

const columnHelper = createColumnHelper<PointItem>();

function PointSettingsPage() {
  const navigate = useNavigate();
  const [items, setItems] = useState<PointItem[]>([]);
  // State: Sort
  const [sorting, setSorting] = useState<SortingState>([]);
  // State: Filters
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([]);

  useEffect(() => {
    ensureDefaultPointItems();
    (async () => {
      const data = await listPointItems();
      setItems(data);
    })();
  }, []);

  // 활성화 체크박스 변경 핸들러
  const handleActiveChange = async (row: PointItem, checked: boolean) => {
    const updated = { ...row, active: checked };
    await upsertPointItem(updated);
    const refreshed = await listPointItems();
    setItems(refreshed);
  };

  // 테이블 컬럼 정의
  const columns = useMemo(() => [
    columnHelper.accessor("title", {
      header: "포인트 부여(회수) 이유",
      size: 250,
      cell: (info) => (
        <span
          style={clickableCellStyle}
          onClick={() => navigate(`/point/item/${info.row.original.id}`)}
        >
          {info.getValue()}
        </span>
      ),
      sortingFn: "alphanumeric",
    }),
    columnHelper.accessor("score", {
      header: "부여(회수) 점수",
      size: 140,
      cell: (info) => {
        const score = info.getValue();
        return <span style={{ fontWeight: 600 }}>{score > 0 ? `+${score}` : score}</span>;
      },
      sortingFn: "alphanumeric",
    }),
    columnHelper.accessor("popoverItems", {
      header: "팝오버 항목",
      size: 140,
      cell: (info) => {
        const items = info.getValue() || [];
        const count = items.length;
        if (count === 0) return <span style={{ color: "#999" }}>없음</span>;
        return (
          <Popover
            placement="bottom"
            trigger="hover"
            reference={
              <Badge
                backgroundColor="#eef2ff"
                textColor="#1d4ed8"
                borderColor="#c7d2fe"
                style={{ cursor: "default" }}
              >
                {count}개 보기
              </Badge>
            }
          >
            <div 
              style={{ 
                background: "#fff", 
                border: "1px solid #e5e7eb", 
                borderRadius: 8, 
                padding: 6, 
                boxShadow: "0 8px 24px rgba(0,0,0,0.12)" 
              }}
            >
              <div style={{ fontWeight: 600, marginBottom: 3, fontSize: "12px" }}>팝오버 항목</div>
              <ul style={{ margin: 0, padding: "0 0 0 16px", fontSize: "12px" }}>
                {items.map((p, idx) => (
                  <li key={idx} style={{ marginBottom: 2 }}>{p}</li>
                ))}
              </ul>
            </div>
          </Popover>
        );
      },
      enableSorting: false,
    }),
    columnHelper.accessor("active", {
      header: "활성",
      size: 80,
      cell: (info) => (
        <input
          type="checkbox"
          checked={info.getValue() ?? true}
          onChange={(e) => handleActiveChange(info.row.original, e.target.checked)}
          style={{ cursor: "pointer", width: 16, height: 16 }}
        />
      ),
      enableSorting: false,
    }),
  ], [navigate]);

  // Table instance
  const table = useReactTable({
    data: items,
    columns,
    state: {
      sorting,
      columnFilters,
    },
    onSortingChange: setSorting,
    onColumnFiltersChange: setColumnFilters,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
  });

  return (
    <PageLayout
      title="포인트 설정"
      actionRows={[
        (
          <button
            key="add-item"
            onClick={() => navigate("/point/item/new")}
            style={{ ...buttonStyle, ...buttonBackgroundColor.save }}
          >
            + 항목 추가
          </button>
        ),
      ]}
    >
      <style>
        {`
          .point-settings-table tbody tr:hover {
            background-color: #f6f8fa;
          }
        `}
      </style>
      <div style={{ overflow: "auto", maxHeight: "calc(100vh - 180px)" }}>
        <table className="point-settings-table" style={tanstackTableStyle}>
          <thead>
            {table.getHeaderGroups().map((headerGroup) => (
              <tr key={headerGroup.id}>
                {headerGroup.headers.map((header) => {
                  const columnId = header.column.id;
                  
                  return (
                    <th
                      key={header.id}
                      style={getTanstackThStyle(false, header.getSize(), {})}
                    >
                      {header.isPlaceholder ? null : (
                        <div>
                          {/* Header with sort */}
                          {header.column.getCanSort() ? (
                            <div
                              onClick={header.column.getToggleSortingHandler()}
                              style={sortHeaderStyle}
                            >
                              {flexRender(
                                header.column.columnDef.header,
                                header.getContext()
                              )}
                              {{
                                asc: <span style={sortArrowStyle}>▲</span>,
                                desc: <span style={sortArrowStyle}>▼</span>,
                              }[header.column.getIsSorted() as string] ?? null}
                            </div>
                          ) : (
                            flexRender(
                              header.column.columnDef.header,
                              header.getContext()
                            )
                          )}
                          
                          {/* Filter row */}
                          <div style={filterContainerStyle}>
                          </div>
                        </div>
                      )}
                    </th>
                  );
                })}
              </tr>
            ))}
          </thead>
          <tbody>
            {table.getRowModel().rows.length === 0 ? (
              <tr>
                <td
                  colSpan={columns.length}
                  style={emptyRowStyle}
                >
                  항목이 없습니다.
                </td>
              </tr>
            ) : (
              table.getRowModel().rows.map((row) => (
                <tr key={row.id} style={tanstackTrStyle}>
                  {row.getVisibleCells().map((cell) => (
                    <td
                      key={cell.id}
                      style={getTanstackTdStyle(false, cell.column.getSize())}
                    >
                      {flexRender(cell.column.columnDef.cell, cell.getContext())}
                    </td>
                  ))}
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </PageLayout>
  );
}

export default PointSettingsPage;
 
